import React from 'react';
import { WifiOff, Wifi, RefreshCw, Send } from 'lucide-react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useServiceWorkerUpdate } from '../hooks/useServiceWorkerUpdate';
import { useOfflineQueue } from '../hooks/useOfflineQueue';

export function OfflineBanner() {
  const { isOnline, wasOffline } = useOnlineStatus();
  const { updateAvailable, applyUpdate, dismiss: dismissUpdate } = useServiceWorkerUpdate();
  const { queueDrained, drainedCount, dismiss: dismissQueue } = useOfflineQueue();

  // Priority: Offline > Back Online > Queue Drained > Update Available
  const showOffline = !isOnline;
  const showBackOnline = isOnline && wasOffline;
  const showQueueDrained = isOnline && !wasOffline && queueDrained;
  const showUpdate = isOnline && !wasOffline && !queueDrained && updateAvailable;

  if (!showOffline && !showBackOnline && !showQueueDrained && !showUpdate) return null;

  return (
    <div
      data-testid="offline-banner"
      className={`fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-all duration-300 animate-slideDown ${
        showOffline
          ? 'bg-slate-800 text-white'
          : showBackOnline
          ? 'bg-emerald-500 text-white'
          : showQueueDrained
          ? 'bg-blue-500 text-white'
          : 'bg-violet-600 text-white'
      }`}
      style={{ fontFamily: 'Fredoka, sans-serif' }}
    >
      {showOffline && (
        <>
          <WifiOff className="w-4 h-4" />
          <span>You&apos;re offline — showing cached content</span>
        </>
      )}
      {showBackOnline && (
        <>
          <Wifi className="w-4 h-4" />
          <span>Back online — content is up to date</span>
        </>
      )}
      {showQueueDrained && (
        <>
          <Send className="w-4 h-4" />
          <span>{drainedCount} offline {drainedCount === 1 ? 'action' : 'actions'} synced successfully</span>
          <button
            onClick={dismissQueue}
            className="ml-2 px-2 py-0.5 rounded-full text-xs bg-white/20 hover:bg-white/30 transition-colors"
          >
            Dismiss
          </button>
        </>
      )}
      {showUpdate && (
        <>
          <RefreshCw className="w-4 h-4" />
          <span>A new version is available</span>
          <button
            data-testid="sw-update-refresh-btn"
            onClick={applyUpdate}
            className="ml-2 px-3 py-0.5 rounded-full text-xs font-semibold bg-white text-violet-700 hover:bg-violet-50 transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={dismissUpdate}
            className="ml-1 px-2 py-0.5 rounded-full text-xs bg-white/20 hover:bg-white/30 transition-colors"
          >
            Later
          </button>
        </>
      )}
    </div>
  );
}
